import { useMemo } from 'react';
import * as R from 'remeda';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { getIDFromUrl } from '@rossum/api-client';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Stack } from '@rossum/ui/material';
import {
  lookupFieldsEnabledSelector,
  memoryFieldsEnabledSelector,
  reasoningFieldsEnabledSelector,
} from '../../../../redux/modules/organizationGroup/selectors';
import { formulaFieldsFeatureSelector } from '../../../pricing/selectors';
import { FieldFormSkeleton } from '../../../queue-settings/fields/components/form/FieldFormSkeleton';
import { ItemContent } from '../../../queue-settings/fields/components/ItemContent';
import { FieldsFormModel } from '../../../queue-settings/fields/form-model/formModels';
import { toSchemaPatchPayload } from '../../../queue-settings/fields/form-model/transformations/toSchema';
import { useFieldManagerDataContext } from '../../hooks/FieldManagerDataContext';
import { usePatchSchemas } from '../../hooks/usePatchSchemas';
import { FieldManagerCreateFieldParams, fieldManagerPath } from '../../routes';
import { CreateFieldHeader } from './CreateFieldHeader';

export const CreateFieldForm = () => {
  const { push, goBack } = useHistory();
  const { params } = useRouteMatch<FieldManagerCreateFieldParams>();

  const {
    schemas,
    queues: allQueues,
    isLoading,
  } = useFieldManagerDataContext();

  const formulaFieldsEnabled = useSelector(formulaFieldsFeatureSelector);

  const reasoningFieldsEnabled = useSelector(reasoningFieldsEnabledSelector);

  const lookupFieldsEnabled = useSelector(lookupFieldsEnabledSelector);

  const memoryFieldsEnabled = useSelector(memoryFieldsEnabledSelector);

  const schema = useMemo(() => {
    const { schemaId } = params;

    if (!schemaId || !Number(schemaId)) return undefined;

    return schemas.find(({ id }) => id === Number(schemaId));
  }, [params, schemas]);

  const queues = useMemo(() => {
    if (!schema) return [];

    const schemaQueueIds = new Set(schema.queues.map(url => getIDFromUrl(url)));

    return allQueues.filter(q => schemaQueueIds.has(q.id));
  }, [schema, allQueues]);

  const parentId = useMemo(() => {
    const { parentId } = params;

    if (!parentId) return undefined;

    return parentId;
  }, [params]);

  const { mutation } = usePatchSchemas();
  const queryClient = useQueryClient();

  const handleSubmit = (data: FieldsFormModel) => {
    if (parentId) {
      const updatedSchema = toSchemaPatchPayload(schema, {
        op: 'add',
        parentId,
        formModel: data,
      });
      if (updatedSchema) {
        mutation.mutate([updatedSchema], {
          onSuccess: ({ fulfilled }) => {
            fulfilled.forEach(newSchema => {
              queryClient.setQueryData(['schema', newSchema.url], newSchema);
            });

            push(fieldManagerPath());
          },
        });
      }
    }
  };

  return (
    <PageLayout
      renderHeader={headerParams =>
        schema ? (
          <CreateFieldHeader
            isLoading={mutation.isLoading}
            goBack={goBack}
            schema={schema}
            {...headerParams}
          />
        ) : null
      }
    >
      <Stack>
        {parentId && schema && !isLoading && queues.length > 0 ? (
          <ItemContent
            parentId={parentId}
            data={null}
            schema={schema}
            queues={queues}
            onSubmit={data => handleSubmit(data)}
            onDelete={R.doNothing}
            onChildrenReorder={R.doNothing}
            onQuickAction={R.doNothing}
            formulaFieldsEnabled={formulaFieldsEnabled}
            reasoningFieldsEnabled={reasoningFieldsEnabled}
            lookupFieldsEnabled={lookupFieldsEnabled}
            memoryFieldsEnabled={memoryFieldsEnabled}
          />
        ) : (
          <FieldFormSkeleton />
        )}
      </Stack>
    </PageLayout>
  );
};
